/**
 * Donors — who gives, and to which partner units.
 *
 * The register the Donor Import fills. Not the same list as Users & Access - Donors,
 * which is the handful of supporters invited to sign in at the donor door; this is
 * everybody the giving report knows about, whether or not they have an account.
 *
 * Kept to owners and administrators, matching the route. A donor's name, address and
 * what they support is the most personal data the archive holds.
 */
import { useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { PageHeader } from '../App'
import { useApi, qs } from '../lib/api'
import {
  Card, Tile, Badge, Notice, Spinner, Empty, SearchInput, useSort, fmtInt,
} from '../components/ui'
import { PagedTable } from '../components/DataTable'
import { useCode } from '../lib/terms'

/** What identifies a donor, for the row's lead cell. */
const donorName = (d) => d.preferred_name || d.email || d.name_key || '—'

export default function Donors({ user }) {
  const code = useCode()
  const [params, setParams] = useSearchParams()
  const unit = params.get('partner_id')
  const [q, setQ] = useState(params.get('q') ?? '')
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(100)
  const { sort, dir, onSort: sortBy } = useSort('unit_count', 'desc', ['unit_count'])
  const onSort = (key) => {
    sortBy(key)
    setPage(1)
  }

  /* Below the hooks, not above them: an early return before useApi would call a
     different number of hooks for an administrator than for an editor. `enabled` is
     what actually keeps the request from being made. */
  const allowed = user.caps.manageSettings
  const query = useApi(
    ['donor-registry', q, unit ?? '', page, limit, sort, dir],
    `donor-registry${qs({ q: q || null, partner_id: unit, page, limit, sort, dir })}`,
    { keepPreviousData: true, enabled: allowed }
  )
  const rows = query.data?.rows ?? []
  const totals = query.data?.totals

  if (!allowed) {
    return (
      <PageHeader title="Donor Register">
        <Notice tone="warn" icon="lock" title="Not available to your role">
          A donor&rsquo;s name, address and the partner units they support is the most
          personal data in the archive, so the register is kept to owners and
          administrators. You are signed in as <strong>{user.roleLabel}</strong>.
        </Notice>
      </PageHeader>
    )
  }

  const search = (v) => {
    setQ(v)
    setPage(1)
    const next = new URLSearchParams(params)
    if (v) next.set('q', v)
    else next.delete('q')
    setParams(next, { replace: true })
  }

  const columns = [
    { key: 'name', label: 'Donor', sortKey: 'name', cellClass: 'small', render: donorName },
    {
      key: 'email',
      label: 'Email',
      sortKey: 'email',
      cellClass: 'small subtle',
      render: (d) => d.email ?? <Badge tone="warn">name only</Badge>,
    },
    { key: 'unit_count', label: 'Units', sortKey: 'unit_count', numeric: true, render: (d) => fmtInt(d.unit_count) },
    {
      key: 'units',
      label: 'Partner units supported',
      cellClass: 'small',
      render: (d) => (
        <div className="row wrap" style={{ gap: 4 }}>
          {d.units.map((u) => (
            <Link
              key={u.partner_id}
              to={`/partners/${u.partner_id}`}
              title={`${u.campaign}${u.display_name ? ` — ${u.display_name}` : ''}`}
            >
              <Badge tone={u.status === 'alumni' ? 'muted' : undefined}>{code(u.s4ca_code)}</Badge>
            </Link>
          ))}
        </div>
      ),
    },
  ]

  return (
    <PageHeader
      title="Donor Register"
      sub="Who gives, and to which partner units — imported from the giving report"
    >
      <div className="col">
        {totals && (
          <div className="grid grid-tiles">
            <Tile label="Donors" value={fmtInt(totals.donors)} />
            <Tile label="Campaign links" value={fmtInt(totals.links)} />
            <Tile label="Partner units supported" value={fmtInt(totals.units)} />
          </div>
        )}

        {!totals?.donors && !query.isLoading && (
          <Notice icon="info" title="The register is empty">
            Nothing has been imported yet. <Link to="/settings/donor-import">Import Donors</Link>{' '}
            reads a Kindful transaction report and fills this page from it.
          </Notice>
        )}

        <SearchInput
          value={q}
          onChange={search}
          placeholder="Search a donor, an address, or an S4CA code…"
          aria-label="Search the donor register"
        />
        {unit && (
          <Notice icon="info" title="Showing one partner unit's donors">
            <button
              className="btn btn-sm"
              onClick={() => {
                const next = new URLSearchParams(params)
                next.delete('partner_id')
                setParams(next, { replace: true })
                setPage(1)
              }}
            >
              Show every donor
            </button>
          </Notice>
        )}

        {query.isLoading ? (
          <Spinner label="Reading the register…" />
        ) : !rows.length ? (
          <Empty title="Nothing found">No donor matches that search.</Empty>
        ) : (
          <Card
            title={`${fmtInt(query.data.total)} Donor${query.data.total === 1 ? '' : 's'}`}
          >
            <PagedTable
              compact
              data={query.data}
              columns={columns}
              sort={sort}
              dir={dir}
              onSort={onSort}
              onPage={setPage}
              onLimit={(n) => {
                setLimit(n)
                setPage(1)
              }}
            />
          </Card>
        )}
      </div>
    </PageHeader>
  )
}
